import { useCallback, useMemo } from 'react';

import { useInfiniteQuery, useMutation } from '@tanstack/react-query';

import KeyDetailsActivity from 'models/activities/KeyDetails';
import { ActivityType } from 'models/Activity';
import { activityQueryKeys, createActivity, indexActivities } from 'services/api/activity';

import useInvalidateQueries from './useInvalidateQueries';

type UseKeyDetailsParams = {
  locationEpisodeId?: string;
};

export default function useKeyDetails({ locationEpisodeId }: UseKeyDetailsParams) {
  const invalidateQueries = useInvalidateQueries();

  const queryParams = {
    locationEpisodeId,
    type: ActivityType.KEY_DETAILS,
    include: 'createdByUser,createdByGroup',
  };

  const query = useInfiniteQuery({
    queryKey: activityQueryKeys.index(queryParams),
    queryFn: ({ pageParam, signal }) => indexActivities({ ...queryParams, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: !!locationEpisodeId,
  });

  const entries = useMemo(
    () =>
      (query.data?.pages.flatMap((page) => page.data).filter((a) => a instanceof KeyDetailsActivity) ??
        []) as KeyDetailsActivity[],
    [query.data]
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createActivity,
  });

  const postKeyDetails = useCallback(
    async (plaintext: string) => {
      await mutateAsync({
        locationEpisodeId,
        type: ActivityType.KEY_DETAILS,
        text: plaintext,
        plaintext,
        mentions: [],
        attachments: [],
      });

      invalidateQueries({ activities: true });
    },
    [locationEpisodeId, mutateAsync, invalidateQueries]
  );

  return { entries, isLoading: query.isLoading, isPending, postKeyDetails, query };
}
